<template>
  <main class="c_customer_story_matrix" :class="{ 'c_customer_story_matrix--expanded': isExpanded }">
    <div class="c_customer_story_matrix__content" :style="{ maxHeight: isExpanded ? 'none' : '125vh' }">
      <div
        v-for="(block, index) in matrixBlocks"
        :key="index"
        :class="blockClasses(block)"
        :style="blockStyles(block)"
        class="lp__block lp__block_2023"
      >
        <Header
          v-if="block.typeHandle === 'header'"
          :eyebrow="block.eyebrowText"
          :title="block.pageTitle"
          :use-quote-as-subtitle="block.useQuoteAsSubtitle"
          :lead-paragraph="block.leadParagraph"
          :quote="block.quote"
          :quote-author="block.quoteAuthor"
          :quote-author-title="block.quoteAuthorTitle"
          :quote-author-workplace="block.quoteAuthorWorkplace"
          :details-table="block.detailsTable"
        />
        <FeaturedProducts v-if="block.typeHandle === 'featuredProducts'" rounded-corners max-width :data="block" />
        <StoryText
          v-if="block.typeHandle === 'text'"
          :header="block.header"
          :text="block.text"
          :divider="block.sectionDivider"
        />
        <QuoteSlider
          v-if="block.typeHandle === 'blockquote'"
          :slides="block.blockQuote"
          text-alignment="center"
          :theme="block.theme"
          :background-overlay-opacity="block.backgroundOverlayOpacity"
          :background-image="block.backgroundImage"
        />
        <ImageGallery v-if="block.typeHandle === 'imageGallery'" :images="block.images2" />
        <EventCards
          v-if="block.typeHandle === 'eventCards'"
          :events="block.eventCards"
          :section-title="block.pageTitle"
        />
        <DisclaimerFootnote
          v-if="block.disclaimerFootnote"
          :disclaimers="block.disclaimerFootnote"
        ></DisclaimerFootnote>
        <ProductOverview v-if="block.typeHandle === 'productOverviewGallery'" :data="block" />
        <CustomerStoryLinks v-if="block.typeHandle === 'links'" :links-obj="block.linkObject" />
        <RelatedCustomerStories
          v-if="block.typeHandle === 'relatedCustomerStories' && block.customerStories.length"
          :section-title="block.sectionTitle"
          :stories="block.customerStories"
        />
        <PromoBlock
          v-if="block.typeHandle === 'promoBlock'"
          :slides="block.customerStory"
          :block-styling="block.customerStoryStyle"
          :display-title="block.displayTitle"
        />
        <PromoList v-if="block.typeHandle == 'promo' && block.promoEntry.length > 0" :data="block.promoEntry" />
        <section
          v-if="block.typeHandle === 'basicText'"
          class="c_wrapper c_wrapper--950"
          :class="{
            c_grid: block.columns !== '1',
            'lp__block--basicText--2_column': block.columns === '2',
            'lp__block--basicText--3_column': block.columns === '3',
            'c_grid--2': block.columns === '2',
            'c_grid--2-3': block.columns === '3',
          }"
        >
          <LPText
            :button-link-field="parseLinkField(item.primaryCtaBtn, 'superTable').valid ? item : null"
            :eyebrow="item.eyebrow || null"
            :eyebrow-style="block.columns !== '1' ? 'regular' : null"
            :subheader="item.subheader || null"
            :text="item.text || null"
            :text-alignment="item.textAlignment || null"
            :primary-cta-btn="item.primaryCtaBtn || null"
            :primary-cta-btn-label="item.primaryCtaBtnLabel || null"
            :primary-cta-btn-aria-label="item.primaryCtaBtnAriaLabel || null"
            :primary-cta-btn-theme="item.primaryCtaBtnTheme || null"
            :primary-cta-btn-open-in-a-new-window="item.primaryCtaBtnOpenInANewWindow || null"
            :btn-custom-url="item.custom || null"
            :btn-label="item.buttonLabel || null"
            :btn-aria-label="item.ariaLabel || null"
            :entry="item.entry || null"
            :asset="item.asset || null"
            :product-category="item.productCategory || null"
            :open-in-a-new-window="item.openInANewWindow || null"
            v-for="(item, index) in block.textBlocks"
            :key="index"
          />
        </section>
      </div>
    </div>
    <div class="c_customer_story_matrix__read_more" v-if="!isExpanded">
      <button class="c_button_2022 c_button_2022__secondary c_button_2022--text_black" @click="toggleExpansion">
        {{ isExpanded ? 'Show less' : 'Read more' }}
      </button>
    </div>
  </main>
</template>

<script>
import { parseLinkField } from 'JS/global.js';
import CustomerStoryLinks from '../2022_customer_story_links/CustomerStoryLinks.vue';
import DisclaimerFootnote from 'Components/disclaimer_footnote/DisclaimerFootnote.vue';
import EventCards from 'Components/2022_event_cards/EventCards.vue';
import FeaturedProducts from 'Components/2022_featured_products/FeaturedProducts.vue';
import Header from 'Components/2022_customer_story_header/Header.vue';
import ImageGallery from 'Components/2022_image_gallery/ImageGallery.vue';
import LPText from 'Components/lp_text/LPText.vue';
import ProductOverview from 'Components/2022_product_overview_gallery/ProductOverview.vue';
import PromoBlock from 'Components/2023_promo_block/PromoBlock.vue';
import PromoList from 'Components/promo/PromoList.vue';
import QuoteSlider from 'Components/2022_quote_slider/QuoteSlider.vue';
import RelatedCustomerStories from '../2023_related_customer_stories/RelatedCustomerStories.vue';
import StoryText from 'Components/2022_customer_story_text/Text.vue';

export default {
  props: {
    matrixBlocks: Array,
    readMore: Boolean,
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  components: {
    CustomerStoryLinks,
    DisclaimerFootnote,
    EventCards,
    FeaturedProducts,
    Header,
    ImageGallery,
    LPText,
    ProductOverview,
    PromoBlock,
    PromoList,
    QuoteSlider,
    RelatedCustomerStories,
    StoryText,
  },
  methods: {
    toggleExpansion() {
      this.isExpanded = true;
    },
    blockClasses(block) {
      const classes = [`lp__block--${block.typeHandle}`];
      // Add classes based on block type and style fields
      if (block.colors) {
        switch (block.colors) {
          case 'blackTextGrayBackground':
            classes.push('c_box--gray_235');
            break;
          case 'blackTextYellowBackground':
            classes.push('c_box--yellow');
            break;
          case 'whiteTextBlackBackground':
            classes.push('c_box--black');
            classes.push('c_text--white');
            break;
          case 'whiteTextDarkGrayBackground':
            classes.push('c_box--gray_41');
            classes.push('c_text--white');
            break;
          default:
            classes.push('c_box--white');
        }
      } else {
        classes.push('c_box--white');
      }
      return classes;
    },
    blockStyles(block) {
      const styles = {};

      // Add syles based on block type and style fields
      if (block.colors) {
        switch (block.colors) {
          case 'blackTextGrayBackground':
            styles['--block-bg-color'] = 'var(--color-gray_235)';
            break;
          case 'blackTextYellowBackground':
            styles['--block-bg-color'] = 'var(--color-yellow)';
            break;
          case 'whiteTextBlackBackground':
            styles['--block-bg-color'] = 'var(--color-black)';
            break;
          case 'whiteTextDarkGrayBackground':
            styles['--block-bg-color'] = 'var(--color-gray_41)';
            break;
          default:
            styles['--block-bg-color'] = 'var(--color-white)';
        }
      } else {
        styles['--block-bg-color'] = 'var(--color-white)';
      }
      return styles;
    },
    parseLinkField,
  },
  created() {
    if (!this.readMore) {
      this.isExpanded = true;
    }
  },
};
</script>

<style lang="scss">
.c_customer_story_matrix {
  $self: &;

  position: relative;
  overflow: hidden;
  @at-root #{&}--expanded {
    .c_customer_story_matrix__content {
      max-height: none;
    }
  }
  @at-root #{&}__read_more {
    position: relative;
    background-color: $color_white;
    text-align: center;
    padding: px_to_rem(50) 0;
    z-index: 50;
    &:before {
      content: '';
      position: absolute;
      top: px_to_rem(-100);
      left: 0;
      height: px_to_rem(100);
      width: 100%;
      background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    }
  }
  .lp__block--basicText {
    padding-top: 5rem;
  }
}
</style>
